import { useXAi } from '@/context/AiProviderContext';
import type { XAiContextType } from '@/context/AiProviderContext';
import type { SendContent } from '@/types';

export interface UseProviderContextOptions<T> {
  // 组件自身的 props
  props: T;
  // 需要从 Provider 中获取的属性
  providerProps?: (keyof XAiContextType)[];
  // 自定义合并逻辑
  mergeLogic?: (props: T, context: XAiContextType | undefined) => T;
}

export interface UseProviderContextReturn<T> {
  // 合并后的 props
  mergedProps: T;
  // 是否在 Provider 中
  isInProvider: boolean;
  // Provider 上下文
  context: XAiContextType | undefined;
  // 从 Provider 中获取特定属性
  getProviderValue: <K extends keyof XAiContextType>(key: K) => XAiContextType[K] | undefined;
}

// 定义方法调用器的类型
export type MethodCaller<T extends any[] = any[], R = any> = (...args: T) => R;

// 定义 Provider 方法的类型
export interface ProviderMethods {
  isInProvider: boolean;
  sendMessage: MethodCaller<[SendContent], void>;
  stopGeneration: MethodCaller<[], void>;
  clearMessages: MethodCaller<[], void>;
  regenerateLastMessage: MethodCaller<[string], void>;
}

/**
 * 通用的 Provider 上下文 Hook
 * 用于判断组件是否在 XAiProvider 中，并合并相关属性
 */
export const useProviderContext = <T extends Record<string, any>>(
  options: UseProviderContextOptions<T>,
): UseProviderContextReturn<T> => {
  const { props, providerProps, mergeLogic } = options;

  // 获取 Provider 上下文
  const context = useXAi();

  // 判断是否在 Provider 中
  const isInProvider = !!context?.isInProvider;

  // 合并 props 和 context
  let mergedProps: T;
  if (mergeLogic) {
    mergedProps = mergeLogic(props, context);
  } else if (context && isInProvider) {
    mergedProps = { ...props, ...context };
  } else {
    mergedProps = props;
  }

  // 获取 Provider 中的特定值
  const getProviderValue = <K extends keyof XAiContextType>(key: K): XAiContextType[K] | undefined => {
    return context?.[key];
  };

  return {
    mergedProps,
    isInProvider,
    context,
    getProviderValue,
  };
};

/**
 * 专门为 XAiChatbot 设计的 Hook
 */
export const useChatbotContext = (componentProps: any) => {
  return useProviderContext({
    props: componentProps,
    mergeLogic: (props, context) => {
      if (!context?.isInProvider) {
        return props;
      }

      // 智能合并逻辑 - 合并所有 Provider 提供的属性
      const { messages: contextMessages, loading: contextLoading, ...restContext } = context;
      return {
        ...props,
        ...restContext,
        // 优先使用 Provider 的状态，如果没有则使用 props
        messages: contextMessages || props.messages || [],
        loading: contextLoading || props.loading || false,
        // 确保 onSend 等回调函数也被合并
        onSend: context.onSend || props.onSend,
        onClear: context.onClear || props.onClear,
        onStop: context.onStop || props.onStop,
        onSuggestMessageClick: context.onSuggestMessageClick || props.onSuggestMessageClick,
        onMessagesActionsCallback: context.onMessagesActionsCallback || props.onMessagesActionsCallback,
      };
    },
  });
};

/**
 * 通用的 Provider 方法调用 Hook
 */
export const useProviderMethods = (): ProviderMethods => {
  const context = useXAi();

  const callMethod = (methodName: keyof XAiContextType, ...args: any[]) => {
    if (context?.isInProvider && typeof context[methodName] === 'function') {
      return (context[methodName] as Function)(...args);
    }
    return undefined;
  };

  // 创建统一的方法调用器，支持参数转换
  const createMethodCaller = (
    providerMethod: string,
    argsTransformer?: (args: any[]) => any[],
  ): MethodCaller => {
    return (...args: any[]) => {
      // 如果 Provider 方法存在，直接调用并返回
      if (context?.isInProvider && typeof context[providerMethod as keyof XAiContextType] === 'function') {
        const transformedArgs = argsTransformer ? argsTransformer(args) : args;
        return (context[providerMethod as keyof XAiContextType] as Function)(...transformedArgs);
      }
      return undefined;
    };
  };

  return {
    isInProvider: !!context?.isInProvider,
    sendMessage: createMethodCaller('onSend'),
    stopGeneration: createMethodCaller('onStop'),
    clearMessages: createMethodCaller('onClear'),
    regenerateLastMessage: createMethodCaller('onSend'),
  };
};
